import { defineComponent, h } from 'vue';
import { createdAt } from '@/ui/components/activityLogs/common/CreatedAt';
import { ContainsTranslationProps } from '@/ui/components/activityLogs/types';
import { useActivityLog } from '@/ui/components/activityLogs/useActivityLog';
import { t } from '@/ui/i18n/i18n';
import { boldifyString, buildField } from '@/ui/components/activityLogs/helpers';
import { ActivityFieldUpdated } from '@/entities/activity/model';
import { user } from '@/ui/auth/auth';

interface FieldUpdatedProps extends ContainsTranslationProps {
  log: ActivityFieldUpdated;
}

export const FieldUpdated = defineComponent<FieldUpdatedProps>({
  name: 'FieldUpdated',
  props: ['log', 'valuesTranslation', 'fieldsTranslation'],
  setup(props) {
    const {
      isSettingFieldsForFirstTime,
      fieldTranslation,
      valuesToText,
      hasNewValue,
    } = useActivityLog(props.log.field, props.fieldsTranslation, props.valuesTranslation);

    const hasPayload = () => props.log.payload !== null;

    return function render() {
      const username = boldifyString(props.log.user?.name ?? '');

      const message = () => {
        if (!hasPayload()) {
          return h('span', {
            domProps: {
              innerHTML: t('activity-logs.updated-field-without-value', {
                username,
                field: buildField(fieldTranslation.value, user.value.locale),
              }),
            },
          });
        }

        return isSettingFieldsForFirstTime(props.log.payload!)
          ? hasNewValue(props.log.payload!)
            ? h('span', {
              domProps: {
                innerHTML: t('activity-logs.updated-field-with-value', {
                  username,
                  field: buildField(fieldTranslation.value, user.value.locale),
                  value: boldifyString(valuesToText(props.log, 'new') as string),
                }),
              },
            })
            : h('span', {
              domProps: {
                innerHTML: t('activity-logs.updated-field-without-value', {
                  username,
                  field: buildField(fieldTranslation.value, user.value.locale),
                }),
              },
            })
          : h('span', {
            domProps: {
              innerHTML: t('activity-logs.updated-field-with-from-and-to-value', {
                username,
                field: buildField(fieldTranslation.value, user.value.locale),
                from: boldifyString(valuesToText(props.log, 'old') as string),
                to: boldifyString(valuesToText(props.log, 'new') as string),
              }),
            },
          });
      };

      return h('div', { class: 'activity-logs__log' }, [
        createdAt({ props: { log: props.log } }),
        message(),
      ]);
    };
  },
});
